"use client";

import { createContext, use, useMemo, type ReactNode } from "react";
import { useWebglMapSupported } from "@/hooks/use-webgl-map-supported";
import { useListingBrowseActions } from "./listing-browse-actions";
import {
  useListingFilterState,
  useListingViewState,
  type ListingBrowseState,
} from "./listing-browse-state";

export type {
  PriceRange,
  SpecFilter,
  BrowseViewMode,
  ListingIntentFilter,
} from "./listing-browse-types";

type ListingBrowseContextValue = ReturnType<typeof useListingFilterState> &
  ReturnType<typeof useListingViewState> &
  ReturnType<typeof useListingBrowseActions> & {
    mapAvailable: boolean;
    lockedProductType: string | null;
    pageTitle: string | null;
    saleOnly: boolean;
  };

const ListingBrowseContext = createContext<ListingBrowseContextValue | null>(
  null,
);

export interface ListingBrowseProviderProps {
  children: ReactNode;
  initialState?: Partial<ListingBrowseState>;
  lockedProductType?: string;
  pageTitle?: string;
  saleOnly?: boolean;
}

export function ListingBrowseProvider({
  children,
  initialState,
  lockedProductType,
  pageTitle,
  saleOnly = false,
}: ListingBrowseProviderProps) {
  const filters = useListingFilterState(
    lockedProductType ? { ...initialState, productType: lockedProductType } : initialState,
  );
  const view = useListingViewState(initialState);
  const actions = useListingBrowseActions({ filters, view });
  const mapAvailable = useWebglMapSupported();

  const value = useMemo(
    () => ({
      ...filters,
      ...view,
      ...actions,
      mapAvailable,
      mapVisible: mapAvailable && view.mapVisible,
      lockedProductType: lockedProductType ?? null,
      pageTitle: pageTitle ?? null,
      saleOnly,
    }),
    [filters, view, actions, mapAvailable, lockedProductType, pageTitle, saleOnly],
  );

  return (
    <ListingBrowseContext value={value}>
      {children}
    </ListingBrowseContext>
  );
}

export function useListingBrowseContext() {
  const context = use(ListingBrowseContext);
  if (!context) {
    throw new Error(
      "useListingBrowseContext must be used within a ListingBrowseProvider",
    );
  }
  return context;
}
